import {
  type ARIAFilterSuggestion,
  type FilterProvider,
  FilterValueType,
  getFilterValue,
  getUniqueReactKey,
  isIndexedFilterBlock,
} from '@github-ui/filter-core'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {testIdProps} from '@github-ui/test-id-props'
import {ActionList} from '@primer/react'
import {clsx} from 'clsx'
import {type ReactNode, useMemo} from 'react'

import {
  useFilter,
  useFilterProviders,
  useFilterQueryState,
  useInputDispatch,
  useSuggestionsDispatch,
  useSuggestionsState,
} from '../context'
import {SuggestionItem} from './SuggestionItem'
import styles from './SuggestionsList.module.css'

/**
 * Resolve the provider used to render a suggestion's leading visual. Done here, in the parent, so
 * each `SuggestionItem` can be memoized and avoid subscribing to the filter query context (which
 * republishes on every keystroke). Mirrors the logic the item previously ran inline.
 */
function resolveSuggestionProvider(
  suggestion: ARIAFilterSuggestion,
  filterProviders: Record<string, FilterProvider>,
  activeBlockProvider: FilterProvider | undefined,
): FilterProvider | undefined {
  if (suggestion.type === FilterValueType.Key) {
    const rawValue = getFilterValue(suggestion.value) ?? ''
    const providerKey = rawValue.startsWith('-') ? rawValue.slice(1) : rawValue
    return filterProviders[providerKey]
  }

  if (suggestion.type === 'keyword') return undefined

  return activeBlockProvider
}

export const SuggestionsList = ({id}: {id: string}) => {
  const {id: filterContextId} = useFilter()
  id = id ?? filterContextId

  const {activeSuggestion, isFetchingSuggestions, position, suggestionGroups, suggestionsVisible} =
    useSuggestionsState()
  const {suggestionsListRef} = useSuggestionsDispatch()
  const {suspendFocus} = useInputDispatch()
  const filterProviders = useFilterProviders()
  const {filterQuery} = useFilterQueryState()

  // The provider that renders value suggestions' leading visuals is the active block's provider — a
  // reference into the (stable) `filterProviders` record, so its identity only changes when the
  // active filter key changes, not on every keystroke. Resolving it here keeps the memoized
  // `generatedSuggestions` below from rebuilding while the user types within a value.
  const activeBlock = filterQuery.activeBlock
  const activeBlockProvider = activeBlock && isIndexedFilterBlock(activeBlock) ? activeBlock.provider : undefined

  const skeletonRow = (key: string, animationStyle: 'wave' | 'pulse' = 'wave') => {
    return (
      <div key={key} className={styles.Box_0}>
        <LoadingSkeleton variant="elliptical" width="20px" height="20px" animationStyle={animationStyle} />
        <LoadingSkeleton variant="rounded" width="random" height="16px" animationStyle={animationStyle} />
      </div>
    )
  }

  const blankSlateSkeletons = useMemo(
    () => (
      <div>
        {Array(8)
          .fill(1)
          .map((_, i) => skeletonRow(`loading-skeleton-${i}`))}
      </div>
    ),
    [],
  )

  // When prefetching, we will only show a single loading skeleton
  const partialResultSkeletons = useMemo(() => skeletonRow(`prefetched-loading-skeleton`, 'pulse'), [])

  const showSuggestionsOverlay =
    (suggestionsVisible && suggestionGroups.some(s => s.suggestions.length > 0)) || isFetchingSuggestions

  const generatedSuggestions = useMemo(() => {
    let rowIndex = -1
    const renderedSuggestions: ReactNode[] = []
    for (let groupIndex = 0; groupIndex < suggestionGroups.length; groupIndex++) {
      const suggestionGroup = suggestionGroups[groupIndex]
      if (!suggestionGroup || suggestionGroup.suggestions.length === 0) continue

      // This is a group. Build the row elements in a plain loop so the running `rowIndex` is not
      // mutated from inside a `.map` lambda (which react-compiler cannot compile).
      const groupItems: ReactNode[] = []
      for (const suggestion of suggestionGroup.suggestions) {
        rowIndex++
        groupItems.push(
          <SuggestionItem
            key={getUniqueReactKey(
              'suggestion',
              suggestion.id,
              `${getFilterValue(suggestion.value)}-${suggestion.displayName}-${suggestion.description}`,
            )}
            {...suggestion}
            provider={resolveSuggestionProvider(suggestion, filterProviders, activeBlockProvider)}
            suggestionId={rowIndex}
            active={rowIndex === activeSuggestion}
          />,
        )
      }

      renderedSuggestions.push(
        <ActionList.Group key={getUniqueReactKey('group', suggestionGroup.id)}>
          {suggestionGroup.title && (
            <ActionList.GroupHeading {...testIdProps('suggestions-heading')} aria-hidden="true">
              {suggestionGroup.title}
            </ActionList.GroupHeading>
          )}
          {groupItems}
        </ActionList.Group>,
      )

      const nextGroup = suggestionGroups[groupIndex + 1]
      if (groupIndex < suggestionGroups.length - 1 && (nextGroup?.suggestions ?? []).length > 0) {
        renderedSuggestions.push(<ActionList.Divider key={getUniqueReactKey('divider', suggestionGroup.id)} />)
      }
    }

    return renderedSuggestions
  }, [activeSuggestion, suggestionGroups, filterProviders, activeBlockProvider])

  return (
    <div className={styles.Box_1} {...testIdProps('backdrop-anchor')}>
      {
        <div
          id={`${id}-suggestions`}
          ref={suggestionsListRef}
          className={clsx(styles.Box_3, showSuggestionsOverlay && styles.showOverlay)}
          style={{
            left: `${position ? position.left : 0}px`,
          }}
        >
          <ActionList
            {...testIdProps('filter-results')}
            aria-label={isFetchingSuggestions ? 'Loading Suggestions' : 'Suggestions'}
            role="listbox"
            // Options are all `tabindex="-1"` (nav is via `aria-activedescendant`), so `useFocusZone`
            // would fall back to making the `<ul>` itself tabbable and pull Tab focus into the list.
            disableFocusZone
            // Opt the scrollable, overflowing `<ul>` out of Chrome's keyboard-focusable scroll
            // containers behavior, which would otherwise add it to the Tab order.
            tabIndex={-1}
            id={`${id}-results`}
            className={styles.ActionList_0}
            onMouseDown={e => suspendFocus(e.currentTarget)}
          >
            {showSuggestionsOverlay && suggestionGroups.some(s => s.suggestions.length > 0) && generatedSuggestions}
            {/* Only show one skeleton row when there is at least one suggestionGroup with suggestions in it */}
            {showSuggestionsOverlay && isFetchingSuggestions && (
              <div>
                {suggestionGroups.some(s => s.suggestions.length > 0) ? partialResultSkeletons : blankSlateSkeletons}
              </div>
            )}
          </ActionList>
        </div>
      }
    </div>
  )
}
